import { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";

import { strCode, accessTypeOptions } from "../../../../../consts";
import { notificationService, t } from "../../../../../services";
import { dateUtil, dropdownUtil } from "../../../../../utils";
import { adminRolesConfigUtils } from "../../utils/adminRolesConfigUtils";
import { useGetAdminRoleConfigOptions } from "../../hooks/useGetAdminRoleConfigOptions";
import { useHandleAdminRolesAvailability } from "../../hooks/useHandleAdminRolesAvailability";

import {
  DateRangeInput,
  DropdownInput,
  FlexContainers,
  SmallButton,
  WrapperContainer
} from "../../../../../components";
import { DateRangeInputWrapper } from "../RolesList/styled";

export const AdminRoleForm = ({ roleConfig, onClose, onSaveRole }) => {
  const [selectedAccessType, setSelectedAccessType] = useState(null);

  const [selectedRoles, setSelectedRoles] = useState([]);
  const [selectedApplications, setSelectedApplications] = useState([]);
  const [selectedMarkets, setSelectedMarkets] = useState([]);
  const [fromDate, setFromDate] = useState();
  const [toDate, setToDate] = useState();

  const [isValid, setIsValid] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  const [missingFields, setMissingFields] = useState({});

  const isPrepopulated = useRef(false);

  const { appOptions, marketOptions, roleOptions } = useGetAdminRoleConfigOptions({
    selectedApplications,
    setSelectedMarkets
  });

  const { availableRoleOptions, isApplicationsDisabled, isMarketsDisabled } =
    useHandleAdminRolesAvailability({
      roleOptions,
      selectedAccessType,
      setSelectedRoles,
      setSelectedApplications,
      setSelectedMarkets
    });

  useEffect(() => {
    if (availableRoleOptions?.length === 1) {
      setSelectedRoles(availableRoleOptions);
    }
  }, [availableRoleOptions]);

  const isExipred = useMemo(() => {
    if (roleConfig?.activeTo) {
      const dateDiff = dateUtil.getDateDifferenceInMilliseconds(new Date(), roleConfig?.activeTo);

      return dateDiff > 0;
    }

    return false;
  }, [roleConfig]);

  useEffect(() => {
    const { isValid: valid, invalidFields } = adminRolesConfigUtils.validateConfig(getConfigData());

    setMissingFields(invalidFields);
    setIsValid(valid);
  }, [selectedAccessType, selectedRoles, selectedApplications, selectedMarkets, fromDate, toDate]);

  const getConfigData = () => {
    const data = {
      accessType: selectedAccessType,
      roles: selectedRoles,
      applications: selectedApplications,
      markets: selectedMarkets,
      isApplicationsDisabled,
      isMarketsDisabled,
      activeFrom: fromDate,
      activeTo: toDate
    };

    return data;
  };

  useEffect(() => {
    prepopulateData();
  }, [roleConfig, appOptions?.length, roleOptions?.length, marketOptions?.length]);

  const prepopulateData = () => {
    if (roleConfig && !isPrepopulated.current) {
      if (!selectedAccessType) {
        setSelectedAccessType(roleConfig?.accessType ?? null);
      }

      if (!selectedRoles.length && (roleConfig?.roles ?? []).length) {
        setSelectedRoles(roleConfig.roles ?? []);
      }

      if (!selectedApplications.length && (roleConfig?.applications ?? []).length) {
        setSelectedApplications(roleConfig.applications ?? []);
      }

      if (!selectedMarkets?.length && (roleConfig?.markets ?? []).length) {
        setSelectedMarkets(roleConfig.markets ?? []);
      }

      if (!fromDate) {
        setFromDate(roleConfig?.activeFrom);
      }

      if (!toDate) {
        setToDate(roleConfig?.activeTo);
      }

      isPrepopulated.current = true;
    }
  };

  const resetFormData = () => {
    setSelectedAccessType(null);
    setSelectedRoles([]);
    setSelectedApplications([]);
    setSelectedMarkets([]);

    setFromDate(null);
    setToDate(null);

    setShowErrors(false);
    setMissingFields({});

    isPrepopulated.current = false;
  };

  const onCancel = () => {
    resetFormData();
    onClose();
  };

  const onSubmit = () => {
    if (!showErrors) {
      setShowErrors(true);
    }

    if (!isValid) {
      notificationService.showError({
        title: t(strCode.MISSING_FIELDS),
        message: t(strCode.MISSING_FIELDS_MESSAGE)
      });
    } else {
      const data = getConfigData();

      onSaveRole(data);
      onCancel();
    }
  };

  return (
    <WrapperContainer Container={FlexContainers.FlexColumnStart} width={"382px"} gap={16}>
      <DropdownInput
        id="access-type-selector"
        title={t(strCode.ACCESS_TYPE_LABEL)}
        placeholder={t(strCode.SELECT_PLACEHOLDER)}
        options={accessTypeOptions}
        selectedOption={selectedAccessType}
        onSelectOption={(id) => {
          setSelectedAccessType(accessTypeOptions.find((opt) => opt.id === id));
        }}
      />

      <DropdownInput
        id="roles-selector"
        title={t(strCode.ROLES_LABEL)}
        placeholder={t(strCode.SELECT_PLACEHOLDER)}
        options={availableRoleOptions}
        selectedOptions={selectedRoles}
        onSelectOption={(id) => {
          setSelectedRoles(
            dropdownUtil.updateSelectedOptions(id, selectedRoles, availableRoleOptions, true)
          );
        }}
        hasError={showErrors && missingFields?.roles}
        disabled={!selectedAccessType || availableRoleOptions?.length === 1}
        showCheckboxes
      />

      <DropdownInput
        id="apps-selector"
        title={t(strCode.APPLICATIONS_LABEL)}
        placeholder={t(strCode.SELECT_PLACEHOLDER)}
        options={appOptions}
        selectedOptions={selectedApplications}
        onSelectOption={(id) => {
          setSelectedApplications(
            dropdownUtil.updateSelectedOptions(id, selectedApplications, appOptions, true)
          );
        }}
        hasError={showErrors && missingFields?.applications && !isApplicationsDisabled}
        disabled={!selectedAccessType || isApplicationsDisabled}
        showCheckboxes
      />

      <DropdownInput
        id="markets-selector"
        title={t(strCode.MARKETS_LABEL)}
        placeholder={t(strCode.SELECT_PLACEHOLDER)}
        options={marketOptions}
        selectedOptions={selectedMarkets}
        onSelectOption={(id) => {
          setSelectedMarkets(
            dropdownUtil.updateSelectedOptions(id, selectedMarkets, marketOptions, true)
          );
        }}
        hasError={showErrors && missingFields?.markets && !isMarketsDisabled}
        disabled={!selectedAccessType || isMarketsDisabled || !marketOptions?.length}
        showCheckboxes
      />

      <DateRangeInputWrapper>
        <DateRangeInput
          fromDate={fromDate}
          toDate={toDate}
          setFromDate={setFromDate}
          setToDate={setToDate}
          noTime
          errors={{
            start: showErrors && missingFields?.from,
            end: (showErrors && missingFields?.to) || isExipred
          }}
        />
      </DateRangeInputWrapper>

      <WrapperContainer Container={FlexContainers.FlexRowEnd} width={"100%"} gap={8}>
        <SmallButton secondary onClick={onCancel}>
          {t(strCode.DISCARD_BTN)}
        </SmallButton>
        <SmallButton onClick={onSubmit}>{t(strCode.SUBMIT_BTN)}</SmallButton>
      </WrapperContainer>
    </WrapperContainer>
  );
};

AdminRoleForm.propTypes = {
  roleConfig: PropTypes.arrayOf(
    PropTypes.shape({
      accessType: PropTypes.shape({
        id: PropTypes.string.isRequired,
        value: PropTypes.string.isRequired
      }).isRequired,
      roles: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.string.isRequired,
          value: PropTypes.string.isRequired
        }).isRequired
      ).isRequired,
      applications: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.string.isRequired,
          value: PropTypes.string.isRequired
        }).isRequired
      ).isRequired,
      markets: PropTypes.arrayOf(
        PropTypes.arrayOf(
          PropTypes.shape({ id: PropTypes.string.isRequired, value: PropTypes.string.isRequired })
        ).isRequired
      ).isRequired,
      activeFrom: PropTypes.instanceOf(Date),
      activeTo: PropTypes.instanceOf(Date)
    }).isRequired
  ),
  onClose: PropTypes.func.isRequired,
  onSaveRole: PropTypes.func.isRequired
};
